import BaseService from './BaseService';
import AnalyticsAPIService from "./AnalyticsAPIService";
import AppService from "./AppService";
import Config from "./Config";
import {Browser} from "@capacitor/browser";
import {LocaleService} from "./LocaleService";

/**
 * Helper for Url management.
 */
class UrlService extends BaseService {

    static baseUrl: string;

    /**
     * Gets App Base url
     */
    static getBaseUrl(): string {
        if (!this.baseUrl) {
          this.baseUrl = window.location.href.split('?')[0];
        }
        return this.baseUrl;
    }

    /**
     * Gets App Callback url
     */
    static getCallbackUrl(): string {
      return AppService.isPwa() ?  window.location.origin : this.config().oauth2_redirect_url;
    }

    /**
     * Gets path.
     */
    static getPath(): string {
      return window.location.pathname;
    }

    /**
     * Clean window URL
     */
    static cleanUrl(): void {
      // El segundo parametro de replaceState es el `unused` de la spec, que los
      // navegadores ignoran. Antes se pasaba null; la firma pide string.
      window.history.replaceState(null, '', window.location.pathname);
    }

    /**
     * Open external link (in new window).
     */
    static openExternalUrl(url: string): boolean {
        this.debug("UrlService.openExternalUrl");
        AnalyticsAPIService.sendOutboundLink(url, url);

        return this.doOpenExternal(url);
    }

    /**
     * Open (gronze) site link (in new window).
     *
     * Similar to openExternalUrl but it doesn't report to analytics
     */
    static openSitelUrl(url: string): boolean {
        this.debug("UrlService.openSiteUrl", url);

        return this.doOpenExternal(url);
    }

    /**
     * Open (gronze) site link (in new window).
     *
     * - Adds tracking utm_source utm_medium
     *
     * OJO: hoy no lo llama nadie, y hasta ahora estaba roto. Invocaba a
     * this.openSiteUrl(), que no existe: el metodo se llama openSitelUrl, con
     * una ele de mas. Habria lanzado TypeError si alguien lo hubiera usado. Se
     * corrige la llamada al nombre real, sin tocar el nombre del metodo.
     */
    static openSitePath(path = '/'): boolean {
        this.debug("UrlService.openSitePath", path);

        return this.openSitelUrl(this.buildSiteUrlFromPath(path));
    }

    /**
     * Builds site path, adding tracking parameters.
     */
    static buildSiteUrlFromPath(path = '/'): string {
        const cleanPath = path.split('?')[0];
        const queryString = path.split('?')[1];
        const params = new URLSearchParams(queryString);

        if (!params.has('utm_source')) {
          params.set('utm_source', 'gronze_maps');
        }

        if (!params.has('utm_medium')) {
          params.set('utm_medium', AppService.isInstalled() ? 'pwa_installed' : 'pwa_browser');
        }

        return Config.getSiteUrl() + cleanPath + '?' + params.toString();
    }

    /**
     * Do open external link. Internal API.
     *
     * Also to be used as hitCallback for ReactGA.outboundlink().
     */
    static doOpenExternal(url: string): boolean {
      this.debug("UrlService.doOpenExternal", url);
      window.open(url, '_blank');
      return false;
    }

    /**
     * Reload window Location.
     *
     * @param reason Optional description of the reload reason for logging.
     */
    static reloadWindow(reason = ''): void {
      this.logger().info("UrlService.reloadWindow(" + reason + ")");
      window.location.reload();
    }

    /**
     * Go to URL (Redirect)
     *
     * @param url Full URL
     */
    static redirectToUrl(url: string): void {
        this.debug("UrlService.redirectToUrl", url);
        if (AppService.isPwa()) {
            AppService.setRedirecting();
            window.location.replace(url);
        } else {
            this.openInBrowser(url);
        }
    }

    /**
     * Go to URL (Redirect)
     *
     * @param url Full URL
     */
    static openInfo(url: string): void {
        this.debug("UrlService.openInfo", url);

        this.openExternalUrl(url);
    }

    /**
     * Go to URL using inAppBrowser plugin
     *
     * @param url Full URL
     */
    static async openInBrowser(url: string): Promise<void> {
        await Browser.open({url});
    }

   /**
    * Redirect base URL.
    */
    static redirectBaseUrl(): void {
        return this.redirectToUrl(this.getBaseUrl());
    }

    /**
     * Go to site path, different behavior:
     * - If browser, just redirect.
     * - If installed pwa, open new window.
     */
    static goToSite(path = '/'): boolean {
      const url = LocaleService.translateGronzeLink(this.buildSiteUrlFromPath(path));
      return this.doOpenExternal(url);
    }

    /**
     * Go to web site (gronze)
     *
     * @todo User config.site_url
     */
    static redirectToSite(): void {
        return this.redirectToUrl('https://www.gronze.com/');
    }

    /**
     * Go to web site (gronze) and call logout
     *
     */
    static logout(): void {
        const redirectUrl = encodeURIComponent(this.getCallbackUrl());
        const logoutUrl = `${Config.getSiteUrl()}/app/logout?redirect_url=${redirectUrl}/logout`;

        // redirect the user to the authorize uri
        this.redirectToUrl(logoutUrl);
    }

    /**
     * Initialize service, from index.js
     *
     * @see index.js
     */
    static init(): void {
      this.debug("UrlService.init()");
      // Calculate before anything else.
      this.getBaseUrl();
    }
}

export default UrlService;
